import { useState } from 'react';
import { Tag, Plus, Edit2, Trash2, MessageSquare, Link as LinkIcon, Calendar, Sparkles } from 'lucide-react';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import { useAnnotations, useAddAnnotation, useRemoveAnnotation } from '@/hooks/useAnnotations';
import { formatDate } from '@/utils/formatters';

const ANNOTATION_ICONS = {
  note: MessageSquare,
  tag: Tag,
  date: Calendar,
  link: LinkIcon,
  ai_insight: Sparkles
};

const ANNOTATION_LABELS = {
  note: 'Note',
  tag: 'Tag',
  date: 'Date',
  link: 'Link',
  ai_insight: 'AI Insight'
};

export default function AnnotationsList({ emailId }) {
  const { data: annotations = [], isLoading } = useAnnotations(emailId);
  const addAnnotation = useAddAnnotation();
  const removeAnnotation = useRemoveAnnotation();

  const [isAdding, setIsAdding] = useState(false);
  const [newAnnotation, setNewAnnotation] = useState({ type: 'note', content: '' });

  const handleAdd = async () => {
    if (!newAnnotation.content.trim()) return;

    try {
      await addAnnotation.mutateAsync({
        emailId,
        type: newAnnotation.type,
        content: newAnnotation.content.trim(),
        source: 'user'
      });
      setNewAnnotation({ type: 'note', content: '' });
      setIsAdding(false);
    } catch (err) {
      console.error('Failed to add annotation:', err);
    }
  };

  const handleRemove = async (annotationId) => {
    if (!confirm('Remove this annotation?')) return;

    try {
      await removeAnnotation.mutateAsync(annotationId);
    } catch (err) {
      console.error('Failed to remove annotation:', err);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading annotations...</div>;
  }

  const userAnnotations = annotations.filter(a => a.source === 'user');
  const aiAnnotations = annotations.filter(a => a.source === 'ai');

  return (
    <div className="space-y-4">
      {/* AI Annotations */}
      {aiAnnotations.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            AI Insights
          </h4>
          <div className="space-y-2">
            {aiAnnotations.map(annotation => {
              const Icon = ANNOTATION_ICONS[annotation.type] || MessageSquare;
              return (
                <div key={annotation.id} className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-950/20">
                  <div className="flex items-start gap-2">
                    <Icon className="h-4 w-4 mt-0.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">
                          {ANNOTATION_LABELS[annotation.type]}
                        </Badge>
                      </div>
                      <p className="text-sm">{annotation.content}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(annotation.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* User Annotations */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium">Your Annotations</h4>
          {!isAdding && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsAdding(true)}
              className="h-7 px-2"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add
            </Button>
          )}
        </div>

        {/* Add Form */}
        {isAdding && (
          <div className="p-3 rounded-lg border bg-muted/50 mb-2">
            <select
              value={newAnnotation.type}
              onChange={(e) => setNewAnnotation({ ...newAnnotation, type: e.target.value })}
              className="w-full px-3 py-1.5 text-sm rounded-md border bg-background mb-2"
            >
              <option value="note">Note</option>
              <option value="tag">Tag</option>
              <option value="date">Date</option>
              <option value="link">Link</option>
            </select>
            <textarea
              value={newAnnotation.content}
              onChange={(e) => setNewAnnotation({ ...newAnnotation, content: e.target.value })}
              placeholder={`Enter ${newAnnotation.type}...`}
              className="w-full px-3 py-2 text-sm rounded-md border bg-background resize-none"
              rows={2}
            />
            <div className="flex gap-2 mt-2">
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={!newAnnotation.content.trim() || addAnnotation.isPending}
              >
                Save
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsAdding(false);
                  setNewAnnotation({ type: 'note', content: '' });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* User Annotations List */}
        {userAnnotations.length > 0 ? (
          <div className="space-y-2">
            {userAnnotations.map(annotation => {
              const Icon = ANNOTATION_ICONS[annotation.type] || MessageSquare;
              return (
                <div key={annotation.id} className="p-3 rounded-lg border bg-background">
                  <div className="flex items-start gap-2">
                    <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs">
                          {ANNOTATION_LABELS[annotation.type]}
                        </Badge>
                      </div>
                      <p className="text-sm">{annotation.content}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDate(annotation.created_at)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(annotation.id)}
                      className="h-7 w-7 p-0 flex-shrink-0"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : !isAdding && (
          <p className="text-sm text-muted-foreground">No annotations yet</p>
        )}
      </div>
    </div>
  );
}
